import React from 'react';
import { useIsTeacher } from '@reducers/user/hooks';
import { Tab, TabList } from '@components/Shared/TabsDefaultSkin';
import ShowIf from '@components/Atoms/ShowIf';
import { useSearchParams } from '@utils/hooks/useSearchParams';
import { LessonTabs } from '../Types';
import {
  useTabConditionsResolved,
  useNavigateToLessonTab,
  useShouldShowAdaptivePracticeTab,
  useShouldShowInterventionTab,
  useShouldShowLearnosityAdaptivePracticeTab,
  useShouldShowStandardPracticeTab,
  useShouldShowStudyHallTab,
} from '../hooks';
import AdaptivePracticeTabContent from './AdaptivePracticeTabContent';
import { useGetLessonDetail } from '../hooks/query';
import { useInteractionContext } from '@components/ElementAnalytics/InteractionContext';

const tabLists = [
  {
    id: 1,
    content: '🧑‍🏫 Lesson Plan',
    tabKey: LessonTabs.LessonPlan,
  },
  {
    id: 2,
    content: '🧑‍🏫 Lesson',
    tabKey: LessonTabs.Lesson,
  },
  {
    id: 3,
    content: '👥 Group Activity',
    tabKey: LessonTabs.LessonPractice,
  },
  {
    id: 4,
    content: <AdaptivePracticeTabContent />,
    tabKey: LessonTabs.Practice,
  },
  {
    id: 5,
    content: '👤 Adaptive Practice',
    tabKey: LessonTabs.LearnosityAdaptivePractice,
  },
  {
    id: 6,
    content: '🎯 Intervention',
    tabKey: LessonTabs.Intervention,
  },
  {
    id: 7,
    content: '📚 Study Hall',
    tabKey: LessonTabs.StudyHall,
  },
];

/**
 * Whether a tab is available is tri-state: `true` (show it), `false` (definitively not
 * available for this lesson) or `undefined` (the data that decides it has not arrived
 * yet). The redirect guard below must only act on a definitive `false`. Treating
 * `undefined` as "unavailable" rewrites deep links away before their data resolves, and
 * the rewrite is never undone: the OGA/OLP toast links a student to `?tab=olp`, the guard
 * bounced them to `?tab=lesson`, and once there `lesson` is unconditionally valid so
 * nothing sends them back.
 */
type TabAvailability = boolean | undefined;

const TabNavigator = () => {
  const { getTestId } = useInteractionContext();
  const navigateToLessonTab = useNavigateToLessonTab();
  const { tab } = useSearchParams();
  const { data: lessonDetail } = useGetLessonDetail();
  const isTeacher: boolean = useIsTeacher();
  const shouldShowStandardPracticeTab = useShouldShowStandardPracticeTab();
  const shouldShowAdaptivePracticeTab = useShouldShowAdaptivePracticeTab();
  const shouldShowInterventionTab = useShouldShowInterventionTab();
  const shouldShowStudyHallTab = useShouldShowStudyHallTab();
  const shouldShowAdaptiveLearnosityPracticeTab =
    useShouldShowLearnosityAdaptivePracticeTab();
  const tabConditionsResolved = useTabConditionsResolved();
  // Read as a scalar so the guard's dependencies stay primitive: only the tab actually
  // being visited matters, and only its readiness should retrigger the effect.
  const isCurrentTabResolved = !!tabConditionsResolved[tab];

  // `undefined` until the lesson detail query resolves, so the guard can tell "this
  // lesson has no documents" apart from "we do not know yet".
  const hasLessonDocuments: TabAvailability = lessonDetail
    ? !!lessonDetail.has_documents
    : undefined;

  const conditions = React.useMemo<Record<string, TabAvailability>>(
    () => ({
      [LessonTabs.LessonPlan]: isTeacher,
      [LessonTabs.Lesson]: true,
      [LessonTabs.LessonPractice]: hasLessonDocuments,
      [LessonTabs.Practice]:
        (shouldShowStandardPracticeTab || shouldShowAdaptivePracticeTab) &&
        !shouldShowAdaptiveLearnosityPracticeTab,
      [LessonTabs.Intervention]: shouldShowInterventionTab,
      [LessonTabs.StudyHall]: shouldShowStudyHallTab,
      [LessonTabs.LearnosityAdaptivePractice]: shouldShowAdaptiveLearnosityPracticeTab,
    }),
    [
      shouldShowAdaptiveLearnosityPracticeTab,
      isTeacher,
      hasLessonDocuments,
      shouldShowAdaptivePracticeTab,
      shouldShowInterventionTab,
      shouldShowStandardPracticeTab,
      shouldShowStudyHallTab,
    ],
  );

  React.useEffect(() => {
    // A tab key we do not recognise is invalid no matter what the data says, so it does
    // not need to wait for anything.
    const isKnownTab = Object.prototype.hasOwnProperty.call(conditions, tab);
    if (!isKnownTab) {
      navigateToLessonTab({ tabKey: LessonTabs.Lesson });
      return;
    }
    // Otherwise hold the URL exactly as linked until the sources this particular tab's
    // condition reads have answered. Defaulting the tab on incomplete data is what
    // silently discarded deep links such as the OGA/OLP toast's `?tab=olp`.
    if (!isCurrentTabResolved) {
      return;
    }
    if (conditions[tab] === false) {
      navigateToLessonTab({ tabKey: LessonTabs.Lesson });
    }
  }, [conditions, tab, isCurrentTabResolved]);

  return (
    <TabList style={{ height: '4rem' }} role='tablist' tabIndex={0}>
      {tabLists.map((tabInfo) => (
        <ShowIf key={tabInfo.id} If={conditions[tabInfo.tabKey]}>
          <Tab
            // flag for editing style
            isLessonTab
            dataTestId={getTestId(`tab-${tabInfo.tabKey}`)}
            tabKey={tabInfo.tabKey}
            onClick={() =>
              navigateToLessonTab({
                tabKey: tabInfo.tabKey,
              })
            }
          >
            {tabInfo.content}
          </Tab>
        </ShowIf>
      ))}
    </TabList>
  );
};

export default TabNavigator;
